export interface IncomeFormCapitalGainKey {
    key: string;
    title: string;
    type: 'amount' | 'text';
    order: number;
    types?: string[];
    /** Pair this field's value with another key for display (e.g. area + unit → "100 Decimal"). */
    unitKey?: string;
    /** Hide standalone unit fields; they are shown with their area field. */
    hideStandalone?: boolean;
}

export const CAPITAL_GAIN_TYPE_KEY = 'ICG_TYPE_OF_GAINS';

export const PROPERTY_LAND = 'Transfer_of_Property_Land';
export const PROPERTY_HOUSE = 'Transfer_of_Property_House_Apartment';
export const SIGNING_MONEY = 'Signing_Money_from_Developer';
export const COMPENSATION = 'Compensation_Against_Property_Acquisition';
export const SHARE_LISTED = 'Transfer_of_Share_Securities_of_Listed_Company';
export const SHARE_NOT_LISTED = 'Transfer_of_Share_Securities_of_Not_Listed_Company';
export const SHARE_INDIVIDUAL = 'Transfer_of_Share_Securities_of_Listed_Company_Individual';
export const BUSINESS_UNDERTAKING = 'Transfer_of_Business_Undertaking';
export const OTHER_CAPITAL_GAIN = 'Other_Capital_Gain';
export const PERSONAL_EFFECTS = 'PERSONAL_EFFECTS';
export const OTHER_VALUABLE_ASSETS = 'OTHER_VALUABLE_ASSETS';
export const SPECIFIED_CONTRACT = 'Value_of_Flat_Apartment_Floor_Space';
export const SRO_GAIN = 'Gain_Exempted_or_Reduced_Tax_Rate_by_SRO';

export const PROPERTY_TRANSFER_TYPES = [PROPERTY_LAND, PROPERTY_HOUSE];
export const SHARE_DIRECTOR_TYPES = [SHARE_LISTED, SHARE_NOT_LISTED];
export const OTHER_ASSET_TRANSFER_TYPES = [
    OTHER_CAPITAL_GAIN,
    PERSONAL_EFFECTS,
    OTHER_VALUABLE_ASSETS,
    BUSINESS_UNDERTAKING,
];

/** Exact option labels from capital-gain location dropdown. */
export const CAPITAL_GAIN_LOCATION_LABELS: Record<string, string> = {
    'city-corporation': 'City Corporation Area',
    pouroshova: 'Pouroshova Area',
    'cant-board': 'Cant. Board Area',
    other: 'Other Area',
};

/** Exact UI labels from capital-gain.component.html (25_26 / 26_27). */
export const INCOME_FORM_CAPITAL_GAIN_KEYS: IncomeFormCapitalGainKey[] = [
    // -------- Common --------
    { key: CAPITAL_GAIN_TYPE_KEY, title: 'Type of Gains', type: 'text', order: 1 },

    // -------- Land / House / Signing / Compensation – description --------
    { key: 'ICG_DES_OF_PROPERTY', title: 'Description of the Property', type: 'text', order: 2, types: PROPERTY_TRANSFER_TYPES.concat([SIGNING_MONEY, COMPENSATION]) },
    { key: 'ICG_LAND_AREA_LOCATION', title: 'Location of the property', type: 'text', order: 3, types: PROPERTY_TRANSFER_TYPES },

    // Land / Signing / Compensation – total area + unit
    { key: 'ICG_TOTAL_AREA', title: 'Total Area', type: 'text', order: 4, types: [PROPERTY_LAND, SIGNING_MONEY, COMPENSATION], unitKey: 'ICG_UNIT_TOTALAREA' },
    { key: 'ICG_UNIT_TOTALAREA', title: 'Unit of Total Area', type: 'text', order: 5, types: [PROPERTY_LAND, SIGNING_MONEY, COMPENSATION], hideStandalone: true },

    // House – land / flat area + unit
    { key: 'ICG_LAND_AREA', title: 'Land Area', type: 'text', order: 6, types: [PROPERTY_HOUSE], unitKey: 'ICG_UNIT_LANDAREA' },
    { key: 'ICG_UNIT_LANDAREA', title: 'Unit of Land Area', type: 'text', order: 7, types: [PROPERTY_HOUSE], hideStandalone: true },
    { key: 'ICG_FLAT_BUILDING_AREA', title: 'Flat/Building Area', type: 'text', order: 8, types: [PROPERTY_HOUSE], unitKey: 'ICG_UNIT_FLATAREA' },
    { key: 'ICG_UNIT_FLATAREA', title: 'Unit of Flat/Building Area', type: 'text', order: 9, types: [PROPERTY_HOUSE], hideStandalone: true },

    // Dates / identity – Land & House
    { key: 'ICG_DATE_OF_ACQUISITION', title: 'Date of Acquisition / Purchase', type: 'text', order: 10, types: PROPERTY_TRANSFER_TYPES.concat([SIGNING_MONEY, SHARE_LISTED, SHARE_NOT_LISTED, BUSINESS_UNDERTAKING, OTHER_CAPITAL_GAIN, PERSONAL_EFFECTS, OTHER_VALUABLE_ASSETS, SRO_GAIN]) },
    { key: 'ICG_TIN_OF_BUYER', title: 'TIN of Buyer', type: 'text', order: 11, types: PROPERTY_TRANSFER_TYPES },
    { key: 'ICG_DEED_NO', title: 'Sale Deed No', type: 'text', order: 12, types: PROPERTY_TRANSFER_TYPES },
    { key: 'ICG_DATE_OF_DEED', title: 'Date of Sale Deed', type: 'text', order: 13, types: PROPERTY_TRANSFER_TYPES },
    { key: 'ICG_SUB_REGISTER_OFC', title: 'Sub Registrar Office', type: 'text', order: 14, types: [PROPERTY_LAND] },
    { key: 'ICG_SUB_REGISTER_OFC', title: 'Sub Register Office', type: 'text', order: 14.1, types: [PROPERTY_HOUSE] },

    // Bank (on detailsEntities as non-ICG keys)
    { key: 'bankName', title: 'Bank Name', type: 'text', order: 15, types: PROPERTY_TRANSFER_TYPES },
    { key: 'branchName', title: 'Bank Branch', type: 'text', order: 16, types: PROPERTY_TRANSFER_TYPES },
    { key: 'bankAccount', title: 'Bank A/C Number', type: 'text', order: 17, types: PROPERTY_TRANSFER_TYPES },

    // Signing money
    { key: 'ICG_NAME_OF_REAL_ESTATE_DEV', title: 'Name of Real Estate Developer', type: 'text', order: 18, types: [SIGNING_MONEY] },
    { key: 'ICG_AGREEMENT_NO', title: 'Agreement No', type: 'text', order: 19, types: [SIGNING_MONEY] },
    { key: 'ICG_AGREEMENT_DATE', title: 'Agreement Date', type: 'text', order: 20, types: [SIGNING_MONEY, SPECIFIED_CONTRACT] },
    { key: 'ICG_MONEY_RCV_DATE', title: 'Signing Money Receive Date', type: 'text', order: 21, types: [SIGNING_MONEY] },

    // Compensation
    { key: 'ICG_DATE_OF_ALIENATION', title: 'Date of Purchase', type: 'text', order: 22, types: [COMPENSATION] },
    { key: 'ICG_ACQUISITION_AUTHORITY', title: 'Acquisition Authority', type: 'text', order: 23, types: [COMPENSATION] },
    { key: 'ICG_ORDER_NO', title: 'Acquisition Order No', type: 'text', order: 24, types: [COMPENSATION] },
    { key: 'ICG_ORDER_DATE', title: 'Acquisition Order Date', type: 'text', order: 25, types: [COMPENSATION] },

    // Share – director / sponsor / not listed
    { key: 'ICG_NAME_OF_COMPANY', title: 'Name of the Company', type: 'text', order: 26, types: SHARE_DIRECTOR_TYPES },
    { key: 'ICG_TRANSFER_NO', title: 'Transfer No', type: 'text', order: 27, types: SHARE_DIRECTOR_TYPES },
    { key: 'ICG_DATE_OF_ALIENATION', title: 'Date of Alienation / Sale', type: 'text', order: 28, types: SHARE_DIRECTOR_TYPES.concat([BUSINESS_UNDERTAKING, OTHER_CAPITAL_GAIN, PERSONAL_EFFECTS, OTHER_VALUABLE_ASSETS, SRO_GAIN]) },

    // Share – individual
    { key: 'ICG_BO_ACCOUNT', title: 'BO ID No.', type: 'text', order: 29, types: [SHARE_INDIVIDUAL] },
    { key: 'ICG_BO_HOUSE', title: 'Brokerage House Name', type: 'text', order: 30, types: [SHARE_INDIVIDUAL] },
    { key: 'ICG_TRANSFER_NO', title: 'Portfolio Statement Reference No.', type: 'text', order: 31, types: [SHARE_INDIVIDUAL] },
    { key: 'ICG_DATE_OF_TRANSFER', title: 'Reference Date', type: 'text', order: 32, types: [SHARE_INDIVIDUAL] },

    // Business / Other / Personal / Valuable / SRO
    { key: 'ICG_DES_OF_ASSET', title: 'Description of the Asset', type: 'text', order: 33, types: [OTHER_CAPITAL_GAIN, PERSONAL_EFFECTS, OTHER_VALUABLE_ASSETS, SRO_GAIN] },
    { key: 'ICG_DES_OF_ASSET', title: 'Description of Business', type: 'text', order: 33.1, types: [BUSINESS_UNDERTAKING] },
    { key: 'ICG_NAME_OF_BUYER', title: 'Name of Buyer', type: 'text', order: 34, types: OTHER_ASSET_TRANSFER_TYPES.concat([SRO_GAIN]) },
    { key: 'ICG_TIN_OR_NID', title: 'TIN/NID', type: 'text', order: 35, types: OTHER_ASSET_TRANSFER_TYPES.concat([SRO_GAIN]) },
    { key: 'ICG_INVOICE_NO', title: 'Invoice No', type: 'text', order: 36, types: OTHER_ASSET_TRANSFER_TYPES.concat([SRO_GAIN]) },

    // Specified contract (26_27)
    { key: 'ICG_DES_OF_ASSET', title: 'Description of property handed over to developer', type: 'text', order: 33.2, types: [SPECIFIED_CONTRACT] },
    { key: 'ICG_NAME_OF_REAL_ESTATE_DEV', title: 'Name of developer', type: 'text', order: 18.1, types: [SPECIFIED_CONTRACT] },
    { key: 'ICG_TIN_OF_BUYER', title: 'TIN of developer', type: 'text', order: 11.1, types: [SPECIFIED_CONTRACT] },
    { key: 'ICG_AGREEMENT_NO', title: 'Contract/ Agreement No.', type: 'text', order: 19.1, types: [SPECIFIED_CONTRACT] },

    // SRO meta
    { key: 'ICG_SRO_NO', title: 'SRO No', type: 'text', order: 37, types: [SRO_GAIN] },
    { key: 'ICG_SRO_YEAR', title: 'Year', type: 'text', order: 38, types: [SRO_GAIN] },
    { key: 'ICG_PARTICULAR_OF_SRO', title: 'Particular of the SRO', type: 'text', order: 39, types: [SRO_GAIN] },

    // -------- Amounts – Land / House --------
    { key: 'ICG_SALE_DEED_VALUE', title: 'Sale Deed Value', type: 'amount', order: 50, types: PROPERTY_TRANSFER_TYPES },
    { key: 'ICG_DEED_EXCESS_AMT', title: 'Excess Amount Received Over Deed Value', type: 'amount', order: 51, types: PROPERTY_TRANSFER_TYPES },
    { key: 'ICG_COST_OF_ACQUISITION', title: 'Cost of Acquisition', type: 'amount', order: 52, types: PROPERTY_TRANSFER_TYPES.concat(OTHER_ASSET_TRANSFER_TYPES).concat([SRO_GAIN]) },
    { key: 'ICG_TAX_APPLICABLE', title: 'Tax Deducted/Collected at Source', type: 'amount', order: 53, types: PROPERTY_TRANSFER_TYPES.concat(SHARE_DIRECTOR_TYPES).concat([SIGNING_MONEY, COMPENSATION]) },
    { key: '__capital_gain_based_on_sale_deed', title: 'Capital Gain (Based On Sale Deed)', type: 'amount', order: 54, types: PROPERTY_TRANSFER_TYPES },
    { key: '__capital_gain_excess_amount', title: 'Capital Gain (Excess Amount Received)', type: 'amount', order: 55, types: PROPERTY_TRANSFER_TYPES },
    { key: 'ICG_CAPITAL_GAIN', title: 'Total Capital Gain', type: 'amount', order: 56, types: PROPERTY_TRANSFER_TYPES },

    // Signing money
    { key: 'ICG_GROSS_AMOUNT', title: 'Gross Amount', type: 'amount', order: 57, types: [SIGNING_MONEY] },

    // Compensation
    { key: 'ICG_PURCHASE_AMOUNT', title: 'Purchase Cost', type: 'amount', order: 58, types: [COMPENSATION] },
    { key: 'ICG_COMPENSATION_AMOUNT', title: 'Compensation Amount', type: 'amount', order: 59, types: [COMPENSATION] },
    { key: 'ICG_GROSS_AMOUNT', title: 'Net Amount', type: 'amount', order: 60, types: [COMPENSATION] },

    // Share – director / not listed
    { key: 'ICG_NUM_OF_SHARES', title: 'No. of Share Transferred', type: 'amount', order: 61, types: SHARE_DIRECTOR_TYPES },
    { key: 'ICG_SALE_VAL_OF_SHARE', title: 'Sale Value of Share', type: 'amount', order: 62, types: SHARE_DIRECTOR_TYPES },
    { key: 'ICG_COST_VAL_OF_SHARE', title: 'Cost Value of Share', type: 'amount', order: 63, types: SHARE_DIRECTOR_TYPES },
    { key: 'ICG_RELATED_EXP', title: 'Related Expenses', type: 'amount', order: 64, types: SHARE_DIRECTOR_TYPES.concat([SHARE_INDIVIDUAL]) },
    { key: 'ICG_REALIZED_GAIN', title: 'Realized Gain', type: 'amount', order: 65, types: SHARE_DIRECTOR_TYPES },

    // Share – individual
    { key: 'ICG_REALIZED_GAIN', title: 'Gross Realized Gain/Loss', type: 'amount', order: 66, types: [SHARE_INDIVIDUAL] },
    { key: 'ICG_TRANSFER_RELATED_EXP', title: 'Net Realized Gain', type: 'amount', order: 67, types: [SHARE_INDIVIDUAL] },

    // Other / Business / Personal / Valuable / SRO amounts
    { key: 'ICG_SALES_VAL_MARKET_VAL', title: 'Sales Value/Fair Market Value', type: 'amount', order: 68, types: OTHER_ASSET_TRANSFER_TYPES.concat([SRO_GAIN]) },
    { key: 'ICG_TRANSFER_RELATED_EXP', title: 'Transfer Related Expenses', type: 'amount', order: 69, types: OTHER_ASSET_TRANSFER_TYPES.concat([SRO_GAIN]) },
    { key: 'ICG_CAPITAL_GAIN', title: 'Capital Gain', type: 'amount', order: 70, types: OTHER_ASSET_TRANSFER_TYPES.concat([SRO_GAIN]) },
    { key: 'ICG_INCOME_EXEMPTED_FROM_TAX', title: 'Income Exempted from Tax', type: 'amount', order: 71, types: [SRO_GAIN] },
    { key: 'ICG_TAXABLE_INCOME', title: 'Taxable Income', type: 'amount', order: 72, types: [SRO_GAIN] },
    { key: 'ICG_APP_TAX_AS_SRO', title: 'Applicable Tax as per SRO', type: 'amount', order: 73, types: [SRO_GAIN] },

    // Specified contract amounts (26_27)
    { key: 'ICG_COMPENSATION_AMOUNT', title: 'Value of flat/ building/ floor space received from developer', type: 'amount', order: 75, types: [SPECIFIED_CONTRACT] },
    { key: 'ICG_COST_OF_ACQUISITION', title: 'Cost of acquisition of the property handed over to developer', type: 'amount', order: 77, types: [SPECIFIED_CONTRACT] },
    { key: 'ICG_CAPITAL_GAIN', title: 'Capital gain on account of specific contract', type: 'amount', order: 78, types: [SPECIFIED_CONTRACT] },
    { key: '__capital_gain_tax', title: 'Capital gain tax', type: 'amount', order: 78.5, types: [SPECIFIED_CONTRACT] },
    { key: 'ICG_ONE_THIRD_PAY', title: 'Do you want to allocate tax', type: 'text', order: 79, types: [SPECIFIED_CONTRACT] },
];

/**
 * Field order for the details view, per gain type.
 * Text fields first, then amount fields (same pattern as PROPERTY_LAND).
 */
export const CAPITAL_GAIN_FIELD_SEQUENCE: Record<string, string[]> = {
    [PROPERTY_LAND]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_PROPERTY',
        'ICG_LAND_AREA_LOCATION',
        'ICG_TOTAL_AREA',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_TIN_OF_BUYER',
        'ICG_DEED_NO',
        'ICG_DATE_OF_DEED',
        'ICG_SUB_REGISTER_OFC',
        'bankName',
        'branchName',
        'bankAccount',
        'ICG_SALE_DEED_VALUE',
        'ICG_DEED_EXCESS_AMT',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TAX_APPLICABLE',
        '__capital_gain_based_on_sale_deed',
        '__capital_gain_excess_amount',
        'ICG_CAPITAL_GAIN',
    ],
    [PROPERTY_HOUSE]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_PROPERTY',
        'ICG_LAND_AREA_LOCATION',
        'ICG_LAND_AREA',
        'ICG_FLAT_BUILDING_AREA',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_TIN_OF_BUYER',
        'ICG_DEED_NO',
        'ICG_DATE_OF_DEED',
        'ICG_SUB_REGISTER_OFC',
        'bankName',
        'branchName',
        'bankAccount',
        'ICG_SALE_DEED_VALUE',
        'ICG_DEED_EXCESS_AMT',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TAX_APPLICABLE',
        '__capital_gain_based_on_sale_deed',
        '__capital_gain_excess_amount',
        'ICG_CAPITAL_GAIN',
    ],
    [SIGNING_MONEY]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_PROPERTY',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_TOTAL_AREA',
        'ICG_NAME_OF_REAL_ESTATE_DEV',
        'ICG_AGREEMENT_NO',
        'ICG_AGREEMENT_DATE',
        'ICG_MONEY_RCV_DATE',
        'ICG_GROSS_AMOUNT',
        'ICG_TAX_APPLICABLE',
    ],
    [COMPENSATION]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_PROPERTY',
        'ICG_DATE_OF_ALIENATION',
        'ICG_TOTAL_AREA',
        'ICG_ACQUISITION_AUTHORITY',
        'ICG_ORDER_NO',
        'ICG_ORDER_DATE',
        'ICG_PURCHASE_AMOUNT',
        'ICG_COMPENSATION_AMOUNT',
        'ICG_GROSS_AMOUNT',
        'ICG_TAX_APPLICABLE',
    ],
    [SHARE_LISTED]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_NAME_OF_COMPANY',
        'ICG_TRANSFER_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_NUM_OF_SHARES',
        'ICG_SALE_VAL_OF_SHARE',
        'ICG_COST_VAL_OF_SHARE',
        'ICG_RELATED_EXP',
        'ICG_REALIZED_GAIN',
        'ICG_TAX_APPLICABLE',
    ],
    [SHARE_NOT_LISTED]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_NAME_OF_COMPANY',
        'ICG_TRANSFER_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_NUM_OF_SHARES',
        'ICG_SALE_VAL_OF_SHARE',
        'ICG_COST_VAL_OF_SHARE',
        'ICG_RELATED_EXP',
        'ICG_REALIZED_GAIN',
        'ICG_TAX_APPLICABLE',
    ],
    [SHARE_INDIVIDUAL]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_BO_ACCOUNT',
        'ICG_BO_HOUSE',
        'ICG_TRANSFER_NO',
        'ICG_DATE_OF_TRANSFER',
        'ICG_REALIZED_GAIN',
        'ICG_RELATED_EXP',
        'ICG_TRANSFER_RELATED_EXP',
    ],
    [SPECIFIED_CONTRACT]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_ASSET',
        'ICG_NAME_OF_REAL_ESTATE_DEV',
        'ICG_TIN_OF_BUYER',
        'ICG_AGREEMENT_NO',
        'ICG_AGREEMENT_DATE',
        'ICG_ONE_THIRD_PAY',
        'ICG_COMPENSATION_AMOUNT',
        'ICG_COST_OF_ACQUISITION',
        'ICG_CAPITAL_GAIN',
        '__capital_gain_tax'
    ],
    [BUSINESS_UNDERTAKING]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_ASSET',
        'ICG_NAME_OF_BUYER',
        'ICG_TIN_OR_NID',
        'ICG_INVOICE_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_SALES_VAL_MARKET_VAL',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TRANSFER_RELATED_EXP',
        'ICG_CAPITAL_GAIN',
    ],
    [OTHER_CAPITAL_GAIN]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_ASSET',
        'ICG_NAME_OF_BUYER',
        'ICG_TIN_OR_NID',
        'ICG_INVOICE_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_SALES_VAL_MARKET_VAL',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TRANSFER_RELATED_EXP',
        'ICG_CAPITAL_GAIN',
    ],
    [PERSONAL_EFFECTS]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_ASSET',
        'ICG_NAME_OF_BUYER',
        'ICG_TIN_OR_NID',
        'ICG_INVOICE_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_SALES_VAL_MARKET_VAL',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TRANSFER_RELATED_EXP',
        'ICG_CAPITAL_GAIN',
    ],
    [OTHER_VALUABLE_ASSETS]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_ASSET',
        'ICG_NAME_OF_BUYER',
        'ICG_TIN_OR_NID',
        'ICG_INVOICE_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_SALES_VAL_MARKET_VAL',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TRANSFER_RELATED_EXP',
        'ICG_CAPITAL_GAIN',
    ],
    [SRO_GAIN]: [
        CAPITAL_GAIN_TYPE_KEY,
        'ICG_DES_OF_ASSET',
        'ICG_NAME_OF_BUYER',
        'ICG_TIN_OR_NID',
        'ICG_INVOICE_NO',
        'ICG_DATE_OF_ACQUISITION',
        'ICG_DATE_OF_ALIENATION',
        'ICG_SRO_NO',
        'ICG_SRO_YEAR',
        'ICG_PARTICULAR_OF_SRO',
        'ICG_SALES_VAL_MARKET_VAL',
        'ICG_COST_OF_ACQUISITION',
        'ICG_TRANSFER_RELATED_EXP',
        'ICG_CAPITAL_GAIN',
        'ICG_INCOME_EXEMPTED_FROM_TAX',
        'ICG_TAXABLE_INCOME',
        'ICG_APP_TAX_AS_SRO',
    ],
};

export function getCapitalGainFieldOrder(gainType: string, key: string, fallbackOrder: number): number {
    const sequence = CAPITAL_GAIN_FIELD_SEQUENCE[gainType];
    if (!sequence) {
        return fallbackOrder;
    }
    const index = sequence.indexOf(key);
    return index === -1 ? fallbackOrder + 1000 : index;
}

/** Exact option titles from capital-gain type-of-gains dropdown. */
export const CAPITAL_GAIN_TYPE_LABELS: Record<string, string> = {
    Transfer_of_Property_Land: 'Transfer of Property (Land Only)',
    Transfer_of_Property_House_Apartment: 'Transfer of Property (House/Apartment)',
    Signing_Money_from_Developer: 'Signing Money from the Developer',
    Compensation_Against_Property_Acquisition: 'Compensation Against Property Acquisition',
    Transfer_of_Share_Securities_of_Listed_Company_Individual: 'Transfer of share of listed Company (Individual)',
    Transfer_of_Share_Securities_of_Listed_Company: 'Transfer of share of listed Company (Director/Sponsor/Placement Shareholder)',
    Transfer_of_Share_Securities_of_Not_Listed_Company: 'Transfer of share of not-listed Company/Private Limited Company',
    Transfer_of_Business_Undertaking: 'Transfer of Business or Undertaking',
    PERSONAL_EFFECTS: 'Transfer of personal effects (Gold, Silver, Gems, Diamond, Coin, Metal, Ornament)',
    OTHER_VALUABLE_ASSETS: 'Transfer of valuable assets (Painting, Antiques, Club Membership)',
    Value_of_Flat_Apartment_Floor_Space: 'Value of Flat/Apartment/Floor Space/Any other non-cash benefits etc.',
    Specified_Contract_with_Developer: 'Capital gain from specified contract with developer/ real state developer',
    Other_Capital_Gain: 'Other Capital Gain',
    Gain_Exempted_or_Reduced_Tax_Rate_by_SRO: 'Gain Subject to Reduced Tax Rate',
};
